import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { Button, Card, Select } from 'antd';
import {
  fetchAssets,
  fetchSessions,
  addInstrumentsToRunningSessions,
  AddInstrumentsToRunningSessions,
} from 'state';
import { StoreState } from '../../../state/storeState';
import {
  fetchAddInstrumentsAssetOptions,
  fetchAddInstrumentsSessionOptions,
} from './addInstrumentsOptionsApi';

type PickerState<Option> = {
  items: Option[];
  loading: boolean;
  error?: string;
};

const usePickerOptions = <Option,>(fetchOptions: () => Promise<Option[]>) => {
  const [state, setState] = useState<PickerState<Option>>({
    items: [],
    loading: true,
  });
  const mounted = useRef(false);
  const requestVersion = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
    };
  }, []);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    const isCurrent = () =>
      mounted.current && version === requestVersion.current;
    if (isCurrent()) {
      setState(current => ({ ...current, loading: true, error: undefined }));
    }
    try {
      const items = await fetchOptions();
      if (isCurrent()) setState({ items, loading: false });
    } catch (error) {
      if (isCurrent()) {
        setState(current => ({
          ...current,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Options could not be loaded.',
        }));
      }
    }
  }, [fetchOptions]);

  return { ...state, load };
};

const AddInstrumentsToRunningSession: React.FC = () => {
  const dispatch = useDispatch();
  const store = useStore<StoreState>();
  const sessions = usePickerOptions(fetchAddInstrumentsSessionOptions);
  const assets = usePickerOptions(fetchAddInstrumentsAssetOptions);
  const loadSessions = sessions.load;
  const loadAssets = assets.load;
  const initialLoadVersion = useRef(0);
  const [selectedSessions, setSelectedSessions] = useState<number[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<number[]>([]);

  useEffect(() => {
    const version = ++initialLoadVersion.current;
    Promise.allSettled([loadSessions(), loadAssets()]).then(() => {
      if (version !== initialLoadVersion.current) return;
      // Preserve the legacy full-cache warming, including after navigation.
      // Only full responses enter shared stores; the picker never waits for them.
      const { app } = store.getState();
      if (!Object.keys(app.underlying_assets.items).length) {
        dispatch(fetchAssets({}));
      }
      if (!Object.keys(app.sessions.items).length) {
        dispatch(fetchSessions({}));
      }
    });
  }, [dispatch, store, loadSessions, loadAssets]);

  return (
    <Card>
      <div style={{ display: 'flex', gap: 10 }}>
        <div>
          <Select
            aria-label="Select session ids"
            mode="multiple"
            allowClear
            loading={sessions.loading}
            disabled={sessions.loading || !!sessions.error}
            style={{ width: '415px' }}
            placeholder="Select session ids"
            value={selectedSessions}
            onChange={value => setSelectedSessions(value as number[])}
          >
            {sessions.items.map(({ id }) => (
              <Select.Option key={id} value={id}>
                {id}
              </Select.Option>
            ))}
          </Select>
          {sessions.error && (
            <div role="alert">
              {sessions.error}{' '}
              <Button size="small" onClick={loadSessions}>
                Retry sessions
              </Button>
            </div>
          )}
        </div>
        <div>
          <Select
            aria-label="Select assets"
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="children"
            loading={assets.loading}
            disabled={assets.loading || !!assets.error}
            style={{ width: '415px' }}
            placeholder="Select assets"
            value={selectedAssets}
            onChange={value => setSelectedAssets(value as number[])}
          >
            {assets.items.map(asset => (
              <Select.Option key={asset.id} value={asset.id}>
                {asset.ric}
              </Select.Option>
            ))}
          </Select>
          {assets.error && (
            <div role="alert">
              {assets.error}{' '}
              <Button size="small" onClick={loadAssets}>
                Retry assets
              </Button>
            </div>
          )}
        </div>
        <Button
          disabled={
            sessions.loading ||
            assets.loading ||
            !!sessions.error ||
            !!assets.error ||
            !selectedSessions.length ||
            !selectedAssets.length
          }
          onClick={() => {
            const data: AddInstrumentsToRunningSessions = {
              assets: selectedAssets,
              sessions: selectedSessions,
            };
            dispatch(addInstrumentsToRunningSessions(data));
          }}
        >
          Add
        </Button>
      </div>
    </Card>
  );
};

export default AddInstrumentsToRunningSession;
